import { QueryKeysEnum } from '@/enums';
import {
  AnalysisBatchCreateDto,
  AnalysisBatchUpdateDto,
  AnalysisEntity,
  AnalysisParams,
  UploadAnalysisSurveyResult,
} from '@/interfaces';
import { newQueryKeys } from '@/queries/queryKeys';
import {
  CreateCommercialSampleKitEntity,
  UploadCommercialSampleKitEntity,
  UploadErrorPayload,
  UploadFileCommercialSampleKitEntity,
} from '@/types';
import { QueryFunctionContext } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import UploadService from '../amili-v3-services/upload.service';
import APIService from './axios.service';
import { BaseService } from './base.service';
import { CustomResponse } from './type';
import { PAGE_DEFAULT } from '@amili-v3/amili-v3-design';

class AnalysisService extends BaseService<
  'analysis',
  AnalysisEntity,
  AnalysisBatchCreateDto
> {
  constructor() {
    super(QueryKeysEnum.ANALYSIS);
  }

  // region Related to Analysis
  uploadAnalysisSurvey = (payload: {
    analysisBatchId: string;
    fileData: FormData;
  }): Promise<Record<string, UploadAnalysisSurveyResult[]>> => {
    const { analysisBatchId, fileData } = payload;
    return APIService.post(
      this.path + `/${analysisBatchId}/upload-survey-data`,
      fileData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  };

  // region Research Analysis
  syncAnalysisBatch = (analysisBatchId: string): Promise<void> =>
    APIService.post(this.path + `/${analysisBatchId}/sync`); // analysisBatchId = displayId

  stopSyncAnalysisBatch = (analysisBatchId?: string): Promise<void> => {
    return APIService.delete(this.path + `/${analysisBatchId}/sync`);
  };

  getResearchAnalysisDetail = (
    context: QueryFunctionContext<
      ReturnType<(typeof newQueryKeys)['analysis']['detail']>
    >,
  ): Promise<AnalysisEntity> => {
    const { id: batchId } = context.queryKey[0] as {
      id: string;
    };
    return APIService.get(`${this.path}/research/${batchId}`);
  };

  getResearchAnalysisBatches = (
    context: QueryFunctionContext<
      ReturnType<(typeof newQueryKeys)['analysis']['list']>
    >,
  ): Promise<CustomResponse<AnalysisEntity[]>> => {
    const { params } = context.queryKey[0];

    return APIService.get(this.path + `/research`, { params });
  };

  updateResearchAnalysisBatch = ({
    data,
    id,
  }: {
    data: AnalysisBatchUpdateDto;
    id: string;
  }): Promise<AnalysisEntity> => {
    return APIService.patch(this.path + `/research/${id}`, data);
  };

  // region Commercial Analysis
  createCommercialAnalysis = (
    payload: AnalysisBatchCreateDto,
  ): Promise<AnalysisEntity> => {
    return APIService.post(this.path + '/commercial', payload);
  };

  getCommercialAnalysisDetail = (
    context: QueryFunctionContext<
      ReturnType<(typeof newQueryKeys)['analysisCommercial']['detail']>
    >,
  ): Promise<AnalysisEntity> => {
    const { id: batchId } = context.queryKey[0] as {
      id: string;
    };
    return APIService.get(`${this.path}/commercial/${batchId}`);
  };

  getInfiniteCommercialAnalysisBatches = (
    context: QueryFunctionContext<
      ReturnType<(typeof newQueryKeys)['analysisCommercial']['list']>
    >,
  ): Promise<CustomResponse<AnalysisEntity[]>> => {
    const { pageParam = PAGE_DEFAULT, queryKey } = context;

    const params = {
      ...(queryKey?.[0]?.params as AnalysisParams),
      page: pageParam as number,
    };

    return APIService.get(this.path + '/commercial', { params });
  };

  deleteCommercialAnalysis = (analysisBatchId: string): Promise<void> => {
    return APIService.delete(this.path + `/${analysisBatchId}/commercial`);
  };

  uploadCommercialSamplesKitFiles = async (
    payload: UploadFileCommercialSampleKitEntity,
  ) => {
    const { analysisBatchId, fileData } = payload;

    const response = (await APIService.post(
      this.path + `/${analysisBatchId}/commercial/files/get-signed-url`,
      fileData.map(file => ({
        filename: file.name,
        filetype: file.type,
      })),
    )) as UploadCommercialSampleKitEntity;
    const presignedURLs = response?.data ?? [];
    const errorData = response?.errorData ?? {};

    if (!isEmpty(errorData)) {
      const error = new Error('Failed to upload file, please try again!');
      (error as unknown as UploadErrorPayload).errorData = errorData;
      throw error;
    }

    await Promise.all(
      presignedURLs.map((signedUrlData, index) =>
        UploadService.putWithFormFileAsync(
          signedUrlData.uploadUrl,
          fileData[index],
          fileData[index].type,
        ),
      ),
    );
  };

  createCommercialSamplesKit = (
    payload: CreateCommercialSampleKitEntity,
  ): Promise<{
    success: string[];
    failed: Record<string, string[]>;
  }> => {
    const { analysisBatchId, data } = payload;
    return APIService.post(
      this.path + `/${analysisBatchId}/commercial/samples`,
      data,
    );
  };

  sendSampleKitsForAnalysis = (payload: {
    analysisBatchId: string;
    kitIds: string[];
  }): Promise<{
    passed: string[];
    failed: Record<string, string[]>;
  }> => {
    const { analysisBatchId, kitIds } = payload;
    return APIService.patch(
      this.path + `/${analysisBatchId}/commercial/send-for-analysis`,
      { kitIds },
    );
  };

  getCommercialAnalysisBatches = (
    context: QueryFunctionContext<
      ReturnType<(typeof newQueryKeys)['analysisCommercial']['list']>
    >,
  ): Promise<CustomResponse<AnalysisEntity[]>> => {
    const { params } = context.queryKey[0];
    return APIService.get(this.path + `/commercial`, { params });
  };

  updateCommercialAnalysisBatch = ({
    data,
    id,
  }: {
    data: AnalysisBatchUpdateDto;
    id: string;
  }): Promise<AnalysisEntity> => {
    return APIService.patch(this.path + `/commercial/${id}`, data);
  };
}
export const analysisService = new AnalysisService();
